import { useRef, useState } from "react";
import { DecisionChip, EvidenceTile, StatusChip, Warn } from "../components";
import { api, DOCUMENT_CATEGORIES, WORK_CATEGORIES, type AreaDetail, type DecisionState, type DocumentCategory, type EvidenceView, type SlotView, type WorkCategory, type WorkSubcategory } from "../lib/api";
import { prepareEvidence, type Prepared } from "../lib/downscale";
import { enqueue, flush } from "../lib/outbox";
import { STATUS as S, fmtAreaM2, fmtBytes, fmtDate, fmtDateTime, placeLine, relTime } from "../lib/status";

/**
 * The approval timeline.
 *
 * This is the reason the app exists. Everything else -- the map, the colours,
 * the address list -- is a way of finding this panel and understanding it fast.
 *
 * Three things it has to get right, and they are the three decisions the
 * tickets were about:
 *
 *  - Nothing is ever edited. A refusal that becomes an agreement is a second
 *    decision that supersedes the first, and the first stays readable forever.
 *    The timeline shows the whole chain, with the superseded ones dimmed.
 *  - Every decision names its author, because this will be argued over.
 *  - The decision is saved before the photo is sent, and the photo can be late.
 *    A "Menunggu" tile is therefore normal, not broken.
 */
export function Timeline({
  detail,
  canRecord,
  canSignOff,
  canEditType,
  canEditPlot,
  canOpenEvidence,
  canDeleteEvidence,
  onChanged,
  say,
  onClose,
}: {
  detail: AreaDetail;
  canRecord: boolean;
  canSignOff: boolean;
  /** Supervisor-only: re-typing an area re-derives what it requires. */
  canEditType: boolean;
  /** Supervisor-only: naming a plot re-derives every area crossing it. */
  canEditPlot: boolean;
  canOpenEvidence: boolean;
  canDeleteEvidence: boolean;
  onChanged: () => Promise<void> | void;
  say: (tone: "red" | "green" | "blue", text: string) => void;
  onClose: () => void;
}) {
  const [open, setOpen] = useState<number | null>(null);
  const [deleting, setDeleting] = useState<EvidenceView | null>(null);

  const pending = detail.slots.flatMap((s) => s.decisions.flatMap((d) => d.evidence)).filter((e) => !e.landed);

  return (
    <aside className="timeline">
      <div className="tl-head">
        <div className="rowflex">
          <h2 className="tl-title">{detail.address_detail || `Kel. ${detail.kelurahan}`}</h2>
          <div className="spacer" />
          <button className="btn btn-sm btn-ghost" onClick={onClose} aria-label="Tutup">
            ✕
          </button>
        </div>
        <div className="tl-addr">{placeLine(detail)}</div>
        <div className="tl-addr mono small">
          {detail.postcode ? `${detail.postcode} · ` : ""}
          {fmtAreaM2(detail.area_m2)} · {detail.plot_count} bidang tanah
          {detail.drawn_by ? ` · digambar ${detail.drawn_by}` : ""}
        </div>
        {/* The document category decides which internal document this area
            requires, so it is read next to the counts, in the same language.
            A legacy area drawn before the category existed shows that
            honestly instead of borrowing a category it never chose. */}
        <div className="tl-addr small" data-document-category={detail.document_category ?? ""}>
          Kategori dokumen: {detail.document_category ?? "belum ditetapkan (area lama)"}
        </div>
        {/* The work axis beside it (#49): what the area is drawn for. A label
            for Dashboard grouping, never a requirement -- so retyping it
            re-derives nothing. A legacy area drawn before the axis existed
            says so honestly instead of borrowing a pair it never chose. */}
        <div
          className="tl-addr small"
          data-work-category={detail.work_category ?? ""}
          data-work-subcategory={detail.work_subcategory ?? ""}
        >
          Kategori pekerjaan:{" "}
          {detail.work_category && detail.work_subcategory
            ? `${detail.work_category} \u2014 ${detail.work_subcategory}`
            : "belum ditetapkan (area lama)"}
        </div>
        {canEditType && (
          <TypeControl detail={detail} onChanged={onChanged} say={say} />
        )}
        {canEditType && (
          <WorkControl detail={detail} onChanged={onChanged} say={say} />
        )}
        {/* The rule the count above obeys, in the language it is read in. It is here
            because a supervisor checking N against the map is the only person who can
            catch the geometry being wrong, and ADR 0006's remedy -- nudging the polygon
            off a fence -- is only available to someone who knows why the count is N. */}
        {detail.plot_count > 0 && (
          <div className="tl-addr small">
            Setiap bidang tanah yang disentuh area ini wajib meminta persetujuan pemiliknya, sekecil
            apa pun bagiannya.
          </div>
        )}
        <div className="tl-stats">
          <StatusChip status={detail.status} />
          <span className="chip chip-granted">
            ✓ {detail.granted_count}/{detail.required_count}
          </span>
          {detail.refused_count > 0 && <span className="chip chip-refused">✕ {detail.refused_count}</span>}
        </div>
        {pending.length > 0 && (
          <div className="mt8">
            <Warn tone="amber">
              {pending.length} bukti belum selesai diunggah. Keputusan sudah tersimpan dan foto akan dikirim
              otomatis.
            </Warn>
          </div>
        )}
      </div>

      {/* Zero plots is blocked, not vacuously permitted: with no bidang tanah
          crossed there is no owner to ask, so the remedy is a re-survey, and the
          polygon reads red through the blocked status above. */}
      {detail.plot_count === 0 && (
        <div className="mt8" style={{ padding: "0 14px 12px" }}>
          <Warn tone="red">
            Area ini tidak melewati bidang tanah apa pun, jadi tidak ada pemilik yang bisa dimintai
            persetujuan. Minta surveyor memeriksa batas di peta dan menggambar ulang area.
          </Warn>
        </div>
      )}

      {detail.slots.map((slot) => (
        <SlotCard
          key={slot.slot_id}
          slot={slot}
          areaId={detail.area_id}
          canRecord={canRecord}
          canEditPlot={canEditPlot}
          canSignOff={canSignOff}
          canOpenEvidence={canOpenEvidence}
          canDeleteEvidence={canDeleteEvidence}
          open={open === slot.slot_id}
          onToggle={() => setOpen(open === slot.slot_id ? null : slot.slot_id)}
          onChanged={onChanged}
          say={say}
          onDeleteEvidence={setDeleting}
        />
      ))}

      {deleting && (
        <DeleteDialog
          ev={deleting}
          onCancel={() => setDeleting(null)}
          onDone={async () => {
            setDeleting(null);
            await onChanged();
          }}
        />
      )}
    </aside>
  );
}

/**
 * Re-categorising an area.
 *
 * Supervisor-only, because this re-derives the requirement set exactly like
 * a geometry change: slots gated out under the new category stop being
 * required. Their rows and decisions stand -- nothing is deleted -- but the
 * timeline stops counting them, so the control says what it is about to do.
 */
function TypeControl({
  detail,
  onChanged,
  say,
}: {
  detail: AreaDetail;
  onChanged: () => Promise<void> | void;
  say: (tone: "red" | "green" | "blue", text: string) => void;
}) {
  const [value, setValue] = useState<DocumentCategory | "">(detail.document_category ?? "");
  const [busy, setBusy] = useState(false);
  const unchanged = value === (detail.document_category ?? "");

  return (
    <div className="rowflex mt8">
      <select
        id="tl-document-category"
        className="input"
        aria-label="Kategori dokumen"
        value={value}
        disabled={busy}
        onChange={(e) => setValue(e.target.value as DocumentCategory | "")}
      >
        <option value="">— Pilih kategori —</option>
        {DOCUMENT_CATEGORIES.map((t) => (
          <option key={t} value={t}>
            {t}
          </option>
        ))}
      </select>
      <button
        className="btn btn-sm"
        disabled={busy || !value || unchanged}
        onClick={async () => {
          if (!value) return;
          setBusy(true);
          try {
            await api.setDocumentCategory(detail.area_id, value);
            await onChanged();
            say("green", `Kategori dokumen diubah ke ${value}. Syarat area dihitung ulang.`);
          } catch (err) {
            say("red", err instanceof Error ? err.message : "Gagal mengubah kategori dokumen.");
          } finally {
            setBusy(false);
          }
        }}
      >
        {busy ? "Menyimpan…" : "Simpan Kategori"}
      </button>
    </div>
  );
}

/**
 * Retyping the work axis.
 *
 * Supervisor-only, like the document recategorise above -- but unlike it,
 * this re-derives nothing: the pair gates no slot, so the requirement set
 * stands untouched and the control says so. Grouping for the Dashboard,
 * and nothing else.
 */
function WorkControl({
  detail,
  onChanged,
  say,
}: {
  detail: AreaDetail;
  onChanged: () => Promise<void> | void;
  say: (tone: "red" | "green" | "blue", text: string) => void;
}) {
  const [category, setCategory] = useState<WorkCategory | "">(detail.work_category ?? "");
  const [sub, setSub] = useState<WorkSubcategory | "">(detail.work_subcategory ?? "");
  const [busy, setBusy] = useState(false);
  const unchanged = category === (detail.work_category ?? "") && sub === (detail.work_subcategory ?? "");
  const subs = category ? WORK_CATEGORIES[category] : [];

  return (
    <div className="rowflex mt8">
      <select
        id="tl-work-category"
        className="input"
        aria-label="Kategori pekerjaan"
        value={category}
        disabled={busy}
        onChange={(e) => {
          setCategory(e.target.value as WorkCategory | "");
          setSub("");
        }}
      >
        <option value="">\u2014 Pilih kategori \u2014</option>
        {(Object.keys(WORK_CATEGORIES) as WorkCategory[]).map((t) => (
          <option key={t} value={t}>
            {t}
          </option>
        ))}
      </select>
      <select
        id="tl-work-subcategory"
        className="input"
        aria-label="Subkategori pekerjaan"
        value={sub}
        disabled={busy || !category}
        onChange={(e) => setSub(e.target.value as WorkSubcategory | "")}
      >
        <option value="">\u2014 Pilih sub \u2014</option>
        {subs.map((s) => (
          <option key={s} value={s}>
            {s}
          </option>
        ))}
      </select>
      <button
        className="btn btn-sm"
        disabled={busy || !category || !sub || unchanged}
        onClick={async () => {
          if (!category || !sub) return;
          setBusy(true);
          try {
            await api.setWorkCategory(detail.area_id, category, sub);
            await onChanged();
            say("green", `Kategori pekerjaan diubah ke ${category} \u2014 ${sub}. Syarat tidak berubah.`);
          } catch (err) {
            say("red", err instanceof Error ? err.message : "Gagal mengubah kategori pekerjaan.");
          } finally {
            setBusy(false);
          }
        }}
      >
        {busy ? "Menyimpan\u2026" : "Simpan Pekerjaan"}
      </button>
    </div>
  );
}

/**
 * Naming an ownerless plot, inline where the block is read.
 *
 * Supervisor-only: replacing the owner set re-expands every area crossing the
 * plot, because each new owner must be asked. The label rides along when given
 * and is left alone when blank, per the presence-based PUT.
 */
function OwnerlessRepair({
  plotId,
  onChanged,
  say,
}: {
  plotId: number;
  onChanged: () => Promise<void> | void;
  say: (tone: "red" | "green" | "blue", text: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState("");
  const [owners, setOwners] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!open) {
    return (
      <div className="slot-actions" style={{ padding: 0, marginTop: 8 }}>
        <button className="btn btn-sm btn-primary" onClick={() => setOpen(true)}>
          + Tambah Pemilik
        </button>
      </div>
    );
  }

  return (
    <div className="record">
      <div className="record-title">Tambah pemilik — Bidang tanah #{plotId}</div>
      <div className="field mt8">
        <label htmlFor={`pl-${plotId}`}>Nama bidang tanah (opsional)</label>
        <input
          id={`pl-${plotId}`}
          className="input"
          placeholder="Mis. Sawah Kulon"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          autoComplete="off"
        />
      </div>
      <div className="field mt8">
        <label htmlFor={`po-${plotId}`}>Nama pemilik — satu per baris</label>
        <textarea
          id={`po-${plotId}`}
          className="input"
          placeholder={"Mis.\nH. Mahmud"}
          value={owners}
          onChange={(e) => setOwners(e.target.value)}
          rows={2}
        />
      </div>
      {error && (
        <div className="mt8">
          <Warn tone="red">{error}</Warn>
        </div>
      )}
      <div className="rowflex mt16">
        <button
          className="btn btn-primary"
          disabled={busy}
          onClick={async () => {
            const names = owners
              .split(/[\n,;]+/)
              .map((n) => n.trim())
              .filter(Boolean);
            if (names.length === 0) {
              setError("Isi minimal satu nama pemilik.");
              return;
            }
            setBusy(true);
            setError(null);
            try {
              await api.updatePlot(plotId, {
                owners: names,
                ...(label.trim() ? { label: label.trim() } : {}),
              });
              setOpen(false);
              setLabel("");
              setOwners("");
              await onChanged();
              say("green", `Bidang tanah #${plotId} kini atas nama ${names.join(", ")}. Syarat area dihitung ulang.`);
            } catch (err) {
              setError(err instanceof Error ? err.message : "Gagal menyimpan pemilik.");
              setBusy(false);
            }
          }}
        >
          {busy ? "Menyimpan…" : "Simpan Pemilik"}
        </button>
        <button
          className="btn btn-ghost"
          onClick={() => {
            setOpen(false);
            setError(null);
          }}
        >
          Batal
        </button>
      </div>
    </div>
  );
}

function SlotCard({
  slot,
  areaId,
  canRecord,
  canEditPlot,
  canSignOff,
  canOpenEvidence,
  canDeleteEvidence,
  open,
  onToggle,
  onChanged,
  say,
  onDeleteEvidence,
}: {
  slot: SlotView;
  areaId: number;
  canRecord: boolean;
  canEditPlot: boolean;
  canSignOff: boolean;
  canOpenEvidence: boolean;
  canDeleteEvidence: boolean;
  open: boolean;
  onToggle: () => void;
  onChanged: () => Promise<void> | void;
  say: (tone: "red" | "green" | "blue", text: string) => void;
  onDeleteEvidence: (ev: EvidenceView) => void;
}) {
  // An ownerless plot's placeholder is a remedy pointer, not an approval: nobody
  // can consent for an unnamed owner, so there is no record control and no
  // history toggle -- only the plot identity and the add-a-name remedy. The
  // plot keeps its `Bidang tanah #<id>` fallback until it is named. A
  // supervisor names it inline here; anyone else is told who to ask.
  if (slot.key === "owner_consent" && slot.owner_id === null) {
    return (
      <div className="slot">
        <div className="slot-head">
          <span className="slot-order no">✕</span>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div className="slot-name">{slot.label}</div>
            <div className="slot-party">Bidang tanah #{slot.plot_id}</div>
            <div className="slot-hint">Bukti: {slot.evidence_hint}</div>
          </div>
        </div>
        <div style={{ padding: "0 14px 12px 46px" }}>
          <Warn tone="red">
            Bidang tanah #{slot.plot_id} belum memiliki nama pemilik. Minta supervisor menambahkan
            nama pemilik agar persetujuannya bisa diminta.
          </Warn>
          {canEditPlot && slot.plot_id !== null && (
            <OwnerlessRepair plotId={slot.plot_id} onChanged={onChanged} say={say} />
          )}
        </div>
      </div>
    );
  }

  const internalBlocked = slot.kind === "internal" && canRecord && !canSignOff;

  return (
    <div className="slot">
      <div className="slot-head">
        <span className={`slot-order ${slot.current_state === "granted" ? "done" : slot.current_state === "refused" ? "no" : ""}`}>
          {slot.current_state === "granted" ? "✓" : slot.current_state === "refused" ? "✕" : "·"}
        </span>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div className="slot-name">{slot.label}</div>
          {/* The plot a consent belongs to, named so two nested consents from
              the same owner read apart. The map canvas stays quiet by design;
              this row is where the name lives. */}
          {slot.plot_id !== null && slot.key === "owner_consent" && (
            <div className="slot-plot">{slot.plot_label ?? `Bidang tanah #${slot.plot_id}`}</div>
          )}
          {slot.owner_name ? (
            <div className="slot-party">
              {slot.owner_name}
              {slot.land_right ? ` · ${slot.land_right}` : ""}
              {slot.land_registered === 0 ? " · tanah mili (girik)" : ""}
            </div>
          ) : (
            <div className="slot-party">Satu per area</div>
          )}
          <div className="slot-hint">Bukti: {slot.evidence_hint}</div>
        </div>
        {slot.current_state && <DecisionChip state={slot.current_state} />}
      </div>

      {slot.decisions.length > 0 && (
        <div className="slot-actions">
          <button className="btn btn-sm btn-ghost" onClick={onToggle}>
            {open ? "▾ Sembunyikan" : `▸ Riwayat (${slot.decisions.length})`}
          </button>
        </div>
      )}

      {open &&
        slot.decisions.map((d, i) => {
          // The tip of the chain is the last thing that happened to this slot.
          const superseded = i < slot.decisions.length - 1;
          return (
            <div className={`dec ${superseded ? "dec-superseded" : ""}`} key={d.id}>
              <div className="dec-top">
                <span className={`dec-state chip chip-${d.state}`}>
                  <span aria-hidden>{d.state === "granted" ? "✓" : "✕"}</span>{" "}
                  {d.state === "granted" ? "Disetujui" : "Ditolak"}
                </span>
                <span className="dec-date">{fmtDate(d.effective_at)}</span>
                {d.id !== slot.current_decision_id && (
                  <span className="chip" title="Digantikan oleh keputusan yang lebih baru">
                    digantikan
                  </span>
                )}
              </div>
              <div className="dec-author">
                dicatat {relTime(d.created_at)} oleh <strong>{d.author}</strong>
                {d.synthetic && <span className="chip"> · Data sintetis</span>}
              </div>
              {d.note && <div className="dec-note">“{d.note}”</div>}
              {d.evidence.length > 0 && (
                <div className="ev-grid">
                  {d.evidence.map((e) => (
                    <EvidenceTile
                      key={e.id}
                      ev={e}
                      canOpen={canOpenEvidence}
                      canDelete={canDeleteEvidence}
                      onDelete={onDeleteEvidence}
                    />
                  ))}
                </div>
              )}
              {superseded && (
                <div className="dec-date mt8">
                  dicatat {fmtDateTime(d.created_at)}
                </div>
              )}
            </div>
          );
        })}

      {!internalBlocked && canRecord && (
        <RecordDecision slot={slot} areaId={areaId} onChanged={onChanged} say={say} />
      )}
      {internalBlocked && (
        <p className="small mute" style={{ padding: "0 14px 12px 46px" }}>
          Persetujuan internal hanya dapat dicatat oleh supervisor.
        </p>
      )}
    </div>
  );
}

/**
 * Recording a decision.
 *
 * Files are prepared *before* the decision is posted, because the post carries
 * the byte sizes that the caps are checked against. Preparing first means a
 * photo that cannot be downscaled fails while the surveyor is still looking at
 * the form, rather than after the decision has already been written.
 *
 * The upload is then fire-and-forget. It is queued in IndexedDB and flushed
 * independently, so the decision is durable before any byte of photo is sent.
 */
function RecordDecision({
  slot,
  areaId,
  onChanged,
  say,
}: {
  slot: SlotView;
  areaId: number;
  onChanged: () => Promise<void> | void;
  say: (tone: "red" | "green" | "blue", text: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [state, setState] = useState<DecisionState>("granted");
  const [note, setNote] = useState("");
  const [when, setWhen] = useState("");
  const [files, setFiles] = useState<Prepared[]>([]);
  const [raw, setRaw] = useState<File[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);

  async function pick(list: FileList | null) {
    if (!list?.length) return;
    setBusy(true);
    setError(null);
    try {
      const out: Prepared[] = [];
      for (const f of Array.from(list).slice(0, 5)) out.push(await prepareEvidence(f));
      setFiles(out);
      setRaw(Array.from(list).slice(0, 5));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Gagal menyiapkan berkas.");
    } finally {
      setBusy(false);
    }
  }

  async function submit() {
    setBusy(true);
    setError(null);
    setWarning(null);
    try {
      const r = await api.recordDecision(areaId, {
        slot_id: slot.slot_id,
        state,
        note: note.trim() || undefined,
        effective_at: when ? new Date(`${when}T09:00:00`).toISOString() : undefined,
        evidence: files.map((f) => ({ kind: f.kind, byte_size: f.blob.size })),
      });

      // The decision exists server-side from here on, whatever happens next.
      let queued = 0;
      for (const [i, evId] of r.evidence_ids.entries()) {
        const f = files[i];
        if (!f) continue;
        try {
          await enqueue({
            evidenceId: evId,
            decisionId: r.decision_id,
            blob: f.blob,
            sha256: f.sha256,
            contentType: f.blob.type || (f.kind === "photo" ? "image/jpeg" : "application/pdf"),
          });
          queued += 1;
        } catch {
          /* IndexedDB refused; the decision is safe and this is reported below */
        }
      }
      void flush().catch(() => undefined);

      setOpen(false);
      setNote("");
      setWhen("");
      setFiles([]);
      setRaw([]);
      if (fileRef.current) fileRef.current.value = "";
      await onChanged();

      if (queued < r.evidence_ids.length) {
        say(
          "red",
          "Keputusan tersimpan, tetapi penyimpanan antrean unggahan gagal. Foto perlu diunggah ulang.",
        );
      } else if (r.evidence_ids.length) {
        say("blue", "Keputusan tersimpan. Bukti dikirim di latar belakang.");
      } else {
        say("green", "Keputusan tersimpan.");
      }
      if (r.warning) setWarning(r.warning);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Gagal menyimpan keputusan.");
    } finally {
      setBusy(false);
    }
  }

  if (!open) {
    return (
      <div className="slot-actions">
        <button className="btn btn-sm btn-primary" onClick={() => setOpen(true)}>
          + Catat Keputusan
        </button>
      </div>
    );
  }

  return (
    <div className="record">
      <div className="record-title">Catat keputusan — {slot.label}</div>

      <div className="grid2">
        <button
          className={`btn ${state === "granted" ? "btn-primary" : ""}`}
          onClick={() => setState("granted")}
        >
          ✓ Disetujui
        </button>
        <button
          className={`btn ${state === "refused" ? "btn-danger" : ""}`}
          onClick={() => setState("refused")}
        >
          ✕ Ditolak
        </button>
      </div>

      {state === "refused" && (
        <p className="small mute mt8">
          Penolakan bisa dibalik. Kalau pemilik berubah pikiran, catat persetujuan baru — catatan lama tetap
          tersimpan.
        </p>
      )}

      <div className="field mt8">
        <label htmlFor={`d-${slot.slot_id}`}>Tanggal persetujuan / penolakan</label>
        <input
          id={`d-${slot.slot_id}`}
          className="input"
          type="date"
          value={when}
          onChange={(e) => setWhen(e.target.value)}
        />
        <span className="small mute">Kosongkan untuk memakai hari ini.</span>
      </div>

      <div className="field mt8">
        <label htmlFor={`n-${slot.slot_id}`}>Catatan</label>
        <textarea
          id={`n-${slot.slot_id}`}
          className="input"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Mis. “Strategi: Sepakat dengan sewa Rp500.000/bulan.”"
        />
      </div>

      <div className="field mt8">
        <label htmlFor={`f-${slot.slot_id}`}>Bukti (foto atau PDF, maks 5 berkas)</label>
        <input
          id={`f-${slot.slot_id}`}
          ref={fileRef}
          className="input"
          type="file"
          accept="image/*,application/pdf"
          capture={undefined}
          multiple
          onChange={(e) => void pick(e.target.files)}
        />
        {busy && files.length === 0 && <span className="small mute">Menyiapkan berkas…</span>}
        {files.length > 0 && (
          <div className="stack mt8" style={{ gap: 4 }}>
            {files.map((f, i) => (
              <div className="rowflex small" key={i}>
                <span className="dim" style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {raw[i]?.name ?? "berkas"}
                </span>
                <span className="mono mute">
                  {fmtBytes(raw[i]?.size ?? 0)} → {fmtBytes(f.blob.size)}
                </span>
              </div>
            ))}
            {files.some((f) => f.note) && (
              <p className="small mute">
                {files.find((f) => f.note)?.note} Data lokasi pada berkas tidak dihapus dalam kasus ini.
              </p>
            )}
          </div>
        )}
      </div>

      {warning && (
        <div className="mt8">
          <Warn tone="amber">{warning}</Warn>
        </div>
      )}
      {error && (
        <div className="mt8">
          <Warn tone="red">{error}</Warn>
        </div>
      )}

      <div className="rowflex mt16">
        <button className="btn btn-primary" disabled={busy} onClick={submit}>
          {busy ? "Menyimpan…" : "Simpan Keputusan"}
        </button>
        <button
          className="btn btn-ghost"
          onClick={() => {
            setOpen(false);
            setError(null);
          }}
        >
          Batal
        </button>
      </div>
    </div>
  );
}

/**
 * Deleting evidence.
 *
 * The pixels go and the record stays: who deleted it, when, which decision it
 * belonged to, and under which basis. UU 27/2022 makes deletion mandatory once
 * the purpose is exhausted, which is why "purpose exhausted" is the default and
 * the alternative has to be argued for.
 */
function DeleteDialog({
  ev,
  onCancel,
  onDone,
}: {
  ev: EvidenceView;
  onCancel: () => void;
  onDone: () => Promise<void> | void;
}) {
  const [basis, setBasis] = useState<"purpose" | "legal">("purpose");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  return (
    <div
      className="map-panel"
      style={{ position: "fixed", inset: 0, margin: "auto", width: "min(420px, 92vw)", height: "fit-content", padding: 16, zIndex: 50 }}
    >
      <strong>Hapus bukti ini?</strong>
      <p className="small dim mt8">
        Berkas dihapus dari penyimpanan. Catatan penghapusan tetap disimpan (siapa, kapan, dasar hukumnya) sesuai
        UU 27/2022.
      </p>

      <div className="stack mt16">
        <label className="rowflex small">
          <input
            type="radio"
            checked={basis === "purpose"}
            onChange={() => setBasis("purpose")}
          />
          Tujuan sudah terpenuhi (dasar penghapusan)
        </label>
        <label className="rowflex small">
          <input
            type="radio"
            checked={basis === "legal"}
            onChange={() => setBasis("legal")}
          />
          Diperlukan sebagai bukti dalam proses hukum
        </label>
        <div className="field">
          <label htmlFor="dn">Catatan</label>
          <textarea id="dn" className="input" value={note} onChange={(e) => setNote(e.target.value)} />
        </div>
      </div>

      {error && (
        <div className="mt8">
          <Warn tone="red">{error}</Warn>
        </div>
      )}

      <div className="rowflex mt16">
        <button
          className="btn btn-danger"
          disabled={busy}
          onClick={async () => {
            setBusy(true);
            setError(null);
            try {
              await api.deleteEvidence(ev.id, basis === "legal" ? "legal_proceedings" : "purpose_exhausted", note);
              await onDone();
            } catch (err) {
              setError(err instanceof Error ? err.message : "Gagal menghapus.");
              setBusy(false);
            }
          }}
        >
          {busy ? "Menghapus…" : "Hapus Bukti"}
        </button>
        <button className="btn btn-ghost" onClick={onCancel}>
          Batal
        </button>
      </div>
    </div>
  );
}

export { S };
